import React from 'react';
import { Typography } from '@mui/material';
import { ResponsiveContainer, LineChart as RechartsLineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';

interface LineChartData {
  [key: string]: string | number;
}

interface LineChartProps extends ChartHeaderProps {
  data: LineChartData[];
  dataKeys: string[];
  xAxisKey: string;
  colors?: string[];
  height?: number;
  graphHeight?: number;
  xAxisTitle?: string;
  yAxisTitle?: string;
  onDetailsClick?: (data?: any) => void;
  gridExist?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({
  title,
  description,
  data,
  dataKeys,
  xAxisKey,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'],
  height = 310,
  graphHeight = 310,
  xAxisTitle,
  yAxisTitle,
  details,
  onDetailsClick,
  gridExist = false
}) => {
  const handlePointClick = (data: any, index: number) => {
    if (onDetailsClick) {
      onDetailsClick(data);
    }
  };
  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      {details?.label && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
          {details.label || ''}
        </Typography>
      )}
      {details?.subLabel && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center' }}>
          {details.subLabel || ''}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={graphHeight}>
        <RechartsLineChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            label={xAxisTitle ? { value: xAxisTitle, position: 'insideBottom', offset: -3, style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6B7280' }}
            label={yAxisTitle ? { value: yAxisTitle, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12, fill: '#374151' } } : undefined}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              fontSize: '12px'
            }}
            cursor={{ stroke: 'rgba(59, 130, 246, 0.2)', strokeWidth: 1 }}
          />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={{ fill: colors[index % colors.length], strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: colors[index % colors.length], cursor: gridExist ? 'pointer' : 'default', onClick: (e: any, payload: any) => handlePointClick(payload.payload, index) }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
      {details?.bottomDescription && (
        <Typography variant="body2" color="textSecondary" gutterBottom sx={{ textAlign: 'center', marginTop: 1 }}>
          {details.bottomDescription || ''}
        </Typography>
      )}
    </ChartCardWrapper>
  );
};

export default LineChart;
